// Signing in (/login) and setting a new password (/reset?token=…): log in with email and password, create an account
// (email, password, the name other players see, an Instagram handle if you like), or ask for a reset link by email
// (?mode=signup and ?mode=forgot open those). Afterwards: where you were going (?next=), your account page by default:
// your place on the waitlist. The page is the homepage's sky at dusk on one side, and a paper card on the other.
import { useEffect, useMemo, useState } from 'react';
import { PASSWORD_MIN } from '../../shared/account';
import { normalizeInstagramHandle, validateDisplayName } from '../../shared/validation';
import { forgotPassword, logIn, resetPassword, signUp, useAccount } from './account';
import { CITIES, REG } from './brand';
import { Wheel } from './landing/Wheel';
import { loadRegistered, openRegister } from './Register';
import { linkProps, navigate, useLocation } from './router';
import './home.css';

type Mode = 'login' | 'signup' | 'forgot' | 'sent' | 'reset';

/** the city names typed out one after another, over the sky */
function useTyped(words: string[]) {
  const [text, setText] = useState('');
  useEffect(() => {
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return setText(words[0]);
    let w = 0;
    let i = 0;
    let back = false;
    let t = 0;
    const step = () => {
      const word = words[w % words.length];
      if (!back) {
        i++;
        setText(word.slice(0, i));
        if (i >= word.length) {
          back = true;
          t = window.setTimeout(step, 1400);
          return;
        }
      } else {
        i--;
        setText(word.slice(0, i));
        if (i <= 0) {
          back = false;
          w++;
        }
      }
      t = window.setTimeout(step, back ? 45 : 95);
    };
    t = window.setTimeout(step, 400);
    return () => clearTimeout(t);
  }, [words]);
  return text;
}

/** where to go afterwards: a path on this site only */
export function nextOf(search: string, fallback = '/account') {
  const n = new URLSearchParams(search).get('next');
  return n && n.startsWith('/') && !n.startsWith('//') ? n : fallback;
}

export function Login() {
  const { path, search } = useLocation();
  const next = useMemo(() => nextOf(search), [search]);
  const token = useMemo(() => new URLSearchParams(search).get('token') ?? '', [search]);
  const me = useAccount();
  const claimed = useMemo(() => loadRegistered(), []);
  const [mode, setMode] = useState<Mode>(() => {
    if (path === '/reset') return 'reset';
    const m = new URLSearchParams(search).get('mode');
    return m === 'signup' ? 'signup' : m === 'forgot' ? 'forgot' : 'login';
  });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [name, setName] = useState(claimed?.name ?? '');
  const [ig, setIg] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // changing your password while logged in: your email's already filled in
  useEffect(() => {
    if (me && !email) setEmail(me.email);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me]);
  const typed = useTyped(useMemo(() => [CITIES.find((c) => c.status === 'live')?.name ?? 'Chandigarh', 'the City Beautiful'], []));

  const switchTo = (m: Mode) => {
    setMode(m);
    setErr(null);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (mode === 'signup') {
      const n = validateDisplayName(name);
      if (!n.ok) return setErr(n.error);
      const h = normalizeInstagramHandle(ig);
      if (!h.ok) return setErr(h.error);
    }
    if ((mode === 'signup' || mode === 'reset') && password.length < PASSWORD_MIN) return setErr(`Use at least ${PASSWORD_MIN} characters for the password.`);
    setBusy(true);
    const r =
      mode === 'login'
        ? await logIn({ email, password })
        : mode === 'signup'
          ? await signUp({ email, password, displayName: name, instagram: ig })
          : mode === 'forgot'
            ? await forgotPassword(email)
            : await resetPassword(token, password);
    setBusy(false);
    if (r.error) return setErr(r.error);
    if (mode === 'forgot') return switchTo('sent');
    navigate(mode === 'reset' ? '/account' : next);
  };

  const pass = (label: string, auto: string) => (
    <label className="kz-lbl">
      {label}
      <div className="kz-field">
        <input className="gx-input" type={show ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} minLength={mode === 'login' ? 1 : PASSWORD_MIN} maxLength={200} required autoComplete={auto} />
        <button type="button" className="kz-eye mono" onClick={() => setShow((s) => !s)} aria-label={show ? 'Hide password' : 'Show password'}>
          {show ? 'Hide' : 'Show'}
        </button>
      </div>
    </label>
  );
  const mail = (
    <label className="kz-lbl">
      Email
      <input className="gx-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" maxLength={254} required autoFocus autoComplete="email" />
    </label>
  );

  return (
    <div className="gx kz kz-auth">
      <div className="kz-grain" aria-hidden />
      <section className="kz-auth-art" aria-hidden>
        <div className="sky" />
        <div className="clouds">
          {Array.from({ length: 7 }, (_, i) => (
            <i key={i} />
          ))}
        </div>
        <a className="kz-mark" {...linkProps('/')} tabIndex={-1}>
          <b>GG</b>
          <Wheel className="w" />
          <b>BHARAT</b>
        </a>
        <div className="say">
          <span className="gx-kicker mono">The waitlist is open</span>
          <p>
            See you in <em>{typed}</em>
            <i className="caret" />
          </p>
        </div>
      </section>
      <section className="kz-auth-side">
        <a className="kz-back mono" {...linkProps('/')}>
          ← ggbharat.com
        </a>
        <div className="kz-auth-card">
          {me && mode !== 'reset' && mode !== 'forgot' && mode !== 'sent' ? (
            <div className="kz-form">
              <span className="gx-kicker mono">Welcome back</span>
              <h1>Namaste, {me.displayName}.</h1>
              <p className="sub">You’re logged in as {me.email}.</p>
              <button type="button" className="gx-btn lg" onClick={() => navigate(next)}>
                Continue <span aria-hidden>→</span>
              </button>
            </div>
          ) : mode === 'sent' ? (
            <div className="kz-form">
              <span className="gx-kicker mono">Check your inbox</span>
              <h1>Link on its way.</h1>
              <p className="sub">If there’s an account for {email}, we’ve sent it a link to set a new password. It works once, for 30 minutes. Check the spam folder too.</p>
              <button type="button" className="gx-btn ghost" onClick={() => switchTo('login')}>
                Back to log in
              </button>
            </div>
          ) : (
            <form className="kz-form" onSubmit={submit}>
              {(mode === 'login' || mode === 'signup') && (
                <div className="kz-tabs" role="tablist">
                  <button type="button" role="tab" aria-selected={mode === 'login'} className={mode === 'login' ? 'on' : ''} onClick={() => switchTo('login')}>
                    Log in
                  </button>
                  <button type="button" role="tab" aria-selected={mode === 'signup'} className={mode === 'signup' ? 'on' : ''} onClick={() => switchTo('signup')}>
                    Create account
                  </button>
                  <i className={`kz-tabbar ${mode}`} aria-hidden />
                </div>
              )}
              {mode === 'login' && (
                <>
                  <h1>Log in.</h1>
                  <p className="sub">See your place on the waitlist and your GG Bharat name. One account for every world: the games, the shows, the events.</p>
                  {mail}
                  {pass('Password', 'current-password')}
                  <button type="button" className="kz-link" onClick={() => switchTo('forgot')}>
                    Forgot password?
                  </button>
                </>
              )}
              {mode === 'signup' && (
                <>
                  <h1>Make your account.</h1>
                  <p className="sub">
                    Free, and it holds your place on the waitlist.{' '}
                    {claimed ? `Use the email you claimed @${claimed.name} with, and the name comes with it.` : 'Your name is how other players will see you.'}
                  </p>
                  <label className="kz-lbl">
                    Your name
                    <input className="gx-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Simran" maxLength={24} required autoComplete="nickname" />
                  </label>
                  {mail}
                  {pass('Password', 'new-password')}
                  <label className="kz-lbl">
                    <span>
                      Instagram <span className="opt">optional</span>
                    </span>
                    <div className="kz-field kz-at">
                      <span aria-hidden>@</span>
                      <input className="gx-input" value={ig} onChange={(e) => setIg(e.target.value)} placeholder="yourhandle" maxLength={64} autoComplete="off" />
                    </div>
                  </label>
                </>
              )}
              {mode === 'forgot' && (
                <>
                  <span className="gx-kicker mono">Forgot password</span>
                  <h1>Reset it by email.</h1>
                  <p className="sub">Enter your account’s email and we’ll send you a link to set a new password.</p>
                  {mail}
                </>
              )}
              {mode === 'reset' && (
                <>
                  <span className="gx-kicker mono">New password</span>
                  <h1>Set a new password.</h1>
                  <p className="sub">At least {PASSWORD_MIN} characters. You’ll be logged in straight after.</p>
                  {!token && <p className="gx-err">This page needs the link from the email.</p>}
                  {pass('New password', 'new-password')}
                </>
              )}
              {err && (
                <p className="gx-err" role="alert">
                  {err}
                </p>
              )}
              <button type="submit" className="gx-btn lg" disabled={busy || (mode === 'reset' && !token)}>
                {busy ? 'One moment…' : mode === 'login' ? 'Log in' : mode === 'signup' ? 'Create account' : mode === 'forgot' ? 'Send reset link' : 'Save and log in'} <span aria-hidden>→</span>
              </button>
              {(mode === 'forgot' || mode === 'reset') && (
                <button type="button" className="gx-btn ghost" onClick={() => switchTo('login')}>
                  Back to log in
                </button>
              )}
              {mode === 'signup' && <p className="kz-privacy">We never ask for your Instagram password. Your email is only for logging in and resetting your password; other players never see it.</p>}
            </form>
          )}
        </div>
        {!claimed && !me && (
          <p className="kz-claim">
            No GG Bharat name yet? Every name is one of a kind.{' '}
            <button type="button" className="kz-link" onClick={() => openRegister()}>
              Join the waitlist · ₹{REG.fee}
            </button>
          </p>
        )}
      </section>
    </div>
  );
}
